import { useState } from "react";
import { Copy, Loader2, Pencil, Search, Trash2, FolderOpen, PlusCircle } from "lucide-react";
import { Button } from "@components/ui/button";
import { Badge } from "@components/ui/badge";
import { Input } from "@components/ui/input";
import { Textarea } from "@components/ui/textarea";
import { Label } from "@components/ui/label";
import { Checkbox } from "@components/ui/checkbox";
import { Switch } from "@components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@components/ui/avatar";
import { Skeleton } from "@components/ui/skeleton";
import { Progress } from "@components/ui/progress";
import { Separator } from "@components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@components/ui/tooltip";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "@components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { useToast } from "@components/ui/use-toast";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@components/ui/accordion";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@components/ui/alert-dialog";
import { Calendar } from "@components/ui/calendar";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@components/ui/command";
import { CurrencyInput } from "@components/ui/currency-input";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@components/ui/hover-card";
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from "@components/ui/input-group";
import { MultiSelectPopover } from "@components/ui/multi-select-popover";
import { MultiSelectFilter } from "@components/ui/multi-select-filter";
import { ReadOnlyField } from "@components/ui/read-only-field";
import { ScrollArea } from "@components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@components/ui/table";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@components/ui/empty";
import { ShowcaseBlock } from "@shared-kernel/primary/shared/design-system/shared/showcase-block";
import { VariantSample } from "@shared-kernel/primary/shared/design-system/shared/variant-sample";

type SampleRow = { id: string; name: string; role: string };

const SAMPLE_ROWS: SampleRow[] = [
  { id: "1", name: "Marie Dubois", role: "Élève" },
  { id: "2", name: "Jean Martin", role: "Professeur" },
  { id: "3", name: "Sophie Laurent", role: "Partenaire" },
];

const MULTI_OPTIONS = [
  { label: "Élève", value: "student" },
  { label: "Professeur", value: "teacher" },
  { label: "Partenaire", value: "provider" },
  { label: "Administrateur", value: "admin" },
];

export const ComponentsSection = () => {
  const [progress, setProgress] = useState(60);
  const [amount, setAmount] = useState<number | "">(120);
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [multi, setMulti] = useState<string[]>(["student"]);
  const [facets, setFacets] = useState<Set<string>>(new Set(["student"]));
  const { toast } = useToast();

  return (
    <div className="space-y-8">
      <ShowcaseBlock
        id="button"
        title="Button"
        description="Variants et tailles disponibles via le composant Button."
        importPath={`import { Button } from "@components/ui/button";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/button.tsx"
      >
        <div className="space-y-4">
          <div className="flex flex-wrap gap-x-4 gap-y-3">
            <VariantSample label="default">
              <Button>Default</Button>
            </VariantSample>
            <VariantSample label="secondary">
              <Button variant="secondary">Secondary</Button>
            </VariantSample>
            <VariantSample label="destructive">
              <Button variant="destructive">Destructive</Button>
            </VariantSample>
            <VariantSample label="success">
              <Button variant="success">Success</Button>
            </VariantSample>
            <VariantSample label="outline">
              <Button variant="outline">Outline</Button>
            </VariantSample>
            <VariantSample label="ghost">
              <Button variant="ghost">Ghost</Button>
            </VariantSample>
            <VariantSample label="link">
              <Button variant="link">Link</Button>
            </VariantSample>
          </div>
          <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
            <VariantSample label="size=xs">
              <Button size="xs">Extra small</Button>
            </VariantSample>
            <VariantSample label="size=sm">
              <Button size="sm">Small</Button>
            </VariantSample>
            <VariantSample label="size=default">
              <Button>Default</Button>
            </VariantSample>
            <VariantSample label="size=lg">
              <Button size="lg">Large</Button>
            </VariantSample>
            <VariantSample label="size=icon">
              <Button size="icon" aria-label="Recherche">
                <Search />
              </Button>
            </VariantSample>
            <VariantSample label="disabled">
              <Button disabled>Disabled</Button>
            </VariantSample>
          </div>
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="button-etats"
        title="Button — États"
        description="États interactifs du Button. Hover et focus sont rendus via leurs classes utilitaires pour permettre l'inspection visuelle sans interaction."
        importPath={`import { Button } from "@components/ui/button";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/button.tsx"
      >
        <div className="flex flex-wrap gap-x-4 gap-y-3">
          <VariantSample label="default">
            <Button>Default</Button>
          </VariantSample>
          <VariantSample label="hover">
            <Button className="bg-primary/90">Hover</Button>
          </VariantSample>
          <VariantSample label="focus">
            <Button className="outline-none ring-2 ring-ring ring-offset-2">Focus</Button>
          </VariantSample>
          <VariantSample label="disabled">
            <Button disabled>Disabled</Button>
          </VariantSample>
          <VariantSample label="loading">
            <Button disabled aria-busy="true">
              <Loader2 className="animate-spin" aria-hidden="true" />
              Chargement
            </Button>
          </VariantSample>
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="badge"
        title="Badge"
        importPath={`import { Badge } from "@components/ui/badge";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/badge.tsx"
      >
        <div className="flex flex-wrap gap-x-4 gap-y-3">
          <VariantSample label="default">
            <Badge>Default</Badge>
          </VariantSample>
          <VariantSample label="secondary">
            <Badge variant="secondary">Secondary</Badge>
          </VariantSample>
          <VariantSample label="destructive">
            <Badge variant="destructive">Destructive</Badge>
          </VariantSample>
          <VariantSample label="success">
            <Badge variant="success">Success</Badge>
          </VariantSample>
          <VariantSample label="warning">
            <Badge variant="warning">Warning</Badge>
          </VariantSample>
          <VariantSample label="info">
            <Badge variant="info">Info</Badge>
          </VariantSample>
          <VariantSample label="outline">
            <Badge variant="outline">Outline</Badge>
          </VariantSample>
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock id="champs-de-formulaire" title="Champs de formulaire" description="Composants de saisie de base.">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="ds-input">Input texte</Label>
            <Input id="ds-input" placeholder="Saisir une valeur" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ds-input-disabled">Input désactivé</Label>
            <Input id="ds-input-disabled" placeholder="Non éditable" disabled />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="ds-textarea">Textarea</Label>
            <Textarea id="ds-textarea" placeholder="Texte multi-lignes" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ds-select">Select</Label>
            <Select>
              <SelectTrigger id="ds-select">
                <SelectValue placeholder="Choisir une option" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="a">Option A</SelectItem>
                <SelectItem value="b">Option B</SelectItem>
                <SelectItem value="c">Option C</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Checkbox id="ds-check" />
              <Label htmlFor="ds-check">Checkbox</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="ds-switch" />
              <Label htmlFor="ds-switch">Switch</Label>
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>Radio group</Label>
            <RadioGroup defaultValue="r1" className="mt-2 flex gap-4">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="r1" id="r1" />
                <Label htmlFor="r1">Option 1</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="r2" id="r2" />
                <Label htmlFor="r2">Option 2</Label>
              </div>
            </RadioGroup>
          </div>
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="input-etats"
        title="Input — États"
        description="États du champ Input. Focus est rendu via la classe utilitaire pour permettre l'inspection visuelle sans interaction."
        importPath={`import { Input } from "@components/ui/input";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/input.tsx"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="ds-input-state-default">default</Label>
            <Input id="ds-input-state-default" placeholder="Saisir une valeur" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ds-input-state-focus">focus</Label>
            <Input id="ds-input-state-focus" placeholder="Saisir une valeur" className="outline-none ring-2 ring-ring ring-offset-2" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ds-input-state-disabled">disabled</Label>
            <Input id="ds-input-state-disabled" placeholder="Non éditable" disabled />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ds-input-state-error">error</Label>
            <Input
              id="ds-input-state-error"
              placeholder="Valeur invalide"
              aria-invalid="true"
              className="border-destructive bg-destructive/5"
            />
            <p className="text-xs text-destructive">Message d'erreur associé au champ.</p>
          </div>
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="avatar"
        title="Avatar"
        importPath={`import { Avatar, AvatarFallback, AvatarImage } from "@components/ui/avatar";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/avatar.tsx"
      >
        <div className="flex items-center gap-4">
          <Avatar>
            <AvatarImage src="" alt="" />
            <AvatarFallback>YF</AvatarFallback>
          </Avatar>
          <Avatar>
            <AvatarFallback>OP</AvatarFallback>
          </Avatar>
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="skeleton"
        title="Skeleton"
        description="État de chargement standard."
        importPath={`import { Skeleton } from "@components/ui/skeleton";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/skeleton.tsx"
      >
        <div className="space-y-2">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-24 w-full" />
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="progress"
        title="Progress"
        importPath={`import { Progress } from "@components/ui/progress";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/progress.tsx"
      >
        <div className="space-y-3">
          <Progress value={progress} />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setProgress(Math.max(0, progress - 10))}>
              -10
            </Button>
            <Button size="sm" variant="outline" onClick={() => setProgress(Math.min(100, progress + 10))}>
              +10
            </Button>
            <span className="text-sm text-muted-foreground">{progress}%</span>
          </div>
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="separator"
        title="Separator"
        importPath={`import { Separator } from "@components/ui/separator";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/separator.tsx"
      >
        <div className="space-y-2">
          <p className="text-sm">Au-dessus</p>
          <Separator />
          <p className="text-sm">En-dessous</p>
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="tabs"
        title="Tabs"
        importPath={`import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/ui/tabs";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/tabs.tsx"
      >
        <Tabs defaultValue="t1" className="w-full">
          <TabsList>
            <TabsTrigger value="t1">Onglet 1</TabsTrigger>
            <TabsTrigger value="t2">Onglet 2</TabsTrigger>
            <TabsTrigger value="t3">Onglet 3</TabsTrigger>
          </TabsList>
          <TabsContent value="t1" className="text-sm text-muted-foreground">
            Contenu de l'onglet 1.
          </TabsContent>
          <TabsContent value="t2" className="text-sm text-muted-foreground">
            Contenu de l'onglet 2.
          </TabsContent>
          <TabsContent value="t3" className="text-sm text-muted-foreground">
            Contenu de l'onglet 3.
          </TabsContent>
        </Tabs>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="tooltip"
        title="Tooltip"
        importPath={`import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@components/ui/tooltip";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/tooltip.tsx"
        useWhen="Étiquette courte explicative au survol d'un bouton icône ou d'un champ."
        avoidWhen="Contenu riche, lien cliquable ou aperçu d'entité — utiliser HoverCard."
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline">Survoler</Button>
            </TooltipTrigger>
            <TooltipContent>Contenu du tooltip</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="card"
        title="Card"
        importPath={`import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@components/ui/card";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/card.tsx"
      >
        <Card>
          <CardHeader>
            <CardTitle>Titre de la carte</CardTitle>
            <CardDescription>Description courte.</CardDescription>
          </CardHeader>
          <CardContent className="text-sm">Contenu principal de la carte.</CardContent>
          <CardFooter className="gap-2">
            <Button size="sm">Action</Button>
            <Button size="sm" variant="outline">
              Annuler
            </Button>
          </CardFooter>
        </Card>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="accordion"
        title="Accordion"
        importPath={`import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@components/ui/accordion";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/accordion.tsx"
      >
        <Accordion type="single" collapsible>
          <AccordionItem value="a1">
            <AccordionTrigger>Section A</AccordionTrigger>
            <AccordionContent>Contenu A.</AccordionContent>
          </AccordionItem>
          <AccordionItem value="a2">
            <AccordionTrigger>Section B</AccordionTrigger>
            <AccordionContent>Contenu B.</AccordionContent>
          </AccordionItem>
        </Accordion>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="dialog"
        title="Dialog"
        importPath={`import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@components/ui/dialog";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/dialog.tsx"
        useWhen="Fenêtre modale basique quand on a besoin d'un contrôle direct du contenu et du footer."
        avoidWhen="Édition d'entité avec footer Annuler/Confirmer standard — préférer Modal qui encapsule ce pattern."
      >
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline">Ouvrir le dialog</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Titre du dialog</DialogTitle>
              <DialogDescription>Description du dialog.</DialogDescription>
            </DialogHeader>
            <p className="text-sm">Contenu principal du dialog.</p>
            <DialogFooter>
              <Button variant="outline">Annuler</Button>
              <Button>Confirmer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="sheet"
        title="Sheet"
        description="Panneau latéral, utilisé pour les drawers de filtre / formulaire."
        importPath={`import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "@components/ui/sheet";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/sheet.tsx"
      >
        <div className="flex flex-wrap gap-2">
          {(["right", "left", "top", "bottom"] as const).map(side => (
            <Sheet key={side}>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm">
                  {side}
                </Button>
              </SheetTrigger>
              <SheetContent side={side}>
                <SheetHeader>
                  <SheetTitle>Sheet {side}</SheetTitle>
                  <SheetDescription>Panneau ouvert depuis le bord {side}.</SheetDescription>
                </SheetHeader>
                <p className="py-4 text-sm text-muted-foreground">Contenu principal du sheet.</p>
                <SheetFooter>
                  <Button size="sm">Valider</Button>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          ))}
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="popover"
        title="Popover"
        importPath={`import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/popover.tsx"
      >
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline">Ouvrir le popover</Button>
          </PopoverTrigger>
          <PopoverContent>
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Filtres rapides</h4>
              <p className="text-xs text-muted-foreground">Contenu ancré sur le déclencheur.</p>
            </div>
          </PopoverContent>
        </Popover>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="dropdown-menu"
        title="Dropdown menu"
        description="Menu d'actions contextuelles sur les lignes de tableau, cartes, etc."
        importPath={`import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@components/ui/dropdown-menu";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/dropdown-menu.tsx"
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">Actions</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <Pencil className="mr-2 size-4" />
              Éditer
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Copy className="mr-2 size-4" />
              Dupliquer
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive focus:text-destructive">
              <Trash2 className="mr-2 size-4" />
              Supprimer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="toast"
        title="Toast"
        description="Notifications non-bloquantes via useToast(). Toaster monté à la racine de l'app."
        importPath={`import { useToast } from "@components/ui/use-toast";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/use-toast.ts"
        useWhen="Confirmation éphémère d'une action utilisateur (sauvegarde, copie, suppression)."
        avoidWhen="Information persistante liée au contenu de la page — préférer Alert."
      >
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => toast({ description: "Action enregistrée." })}>
            Toast par défaut
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              toast({
                variant: "destructive",
                description: "Une erreur est survenue.",
              })
            }
          >
            Toast destructive
          </Button>
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="alert-dialog"
        title="AlertDialog"
        description="Confirmation bloquante — opérations destructives ou irréversibles."
        importPath={`import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@components/ui/alert-dialog";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/alert-dialog.tsx"
      >
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive">Supprimer</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
              <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction>Supprimer</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="calendar"
        title="Calendar"
        description="Sélecteur de date (react-day-picker). Pour un date picker complet, combiner avec Popover."
        importPath={`import { Calendar } from "@components/ui/calendar";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/calendar.tsx"
      >
        <Calendar mode="single" selected={date} onSelect={setDate} className="rounded-md border" />
      </ShowcaseBlock>

      <ShowcaseBlock
        id="command"
        title="Command"
        description="Palette de commandes inline (cmdk). Utilisée pour la recherche globale et les comboboxes."
        importPath={`import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@components/ui/command";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/command.tsx"
      >
        <Command className="rounded-md border">
          <CommandInput placeholder="Tapez pour rechercher..." />
          <CommandList>
            <CommandEmpty>Aucun résultat.</CommandEmpty>
            <CommandGroup heading="Suggestions">
              <CommandItem>Élèves</CommandItem>
              <CommandItem>Professeurs</CommandItem>
              <CommandItem>Masterclasses</CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Actions">
              <CommandItem>Créer un élève</CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="currency-input"
        title="CurrencyInput"
        description="Saisie de montants avec symbole en addon. Valeur typée number | ''."
        importPath={`import { CurrencyInput } from "@components/ui/currency-input";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/currency-input.tsx"
      >
        <div className="max-w-xs space-y-1.5">
          <Label htmlFor="ds-currency">Montant</Label>
          <CurrencyInput id="ds-currency" value={amount} onChange={setAmount} />
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="input-group"
        title="InputGroup"
        description="Composition input + addon (icône, préfixe, suffixe, bouton)."
        importPath={`import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from "@components/ui/input-group";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/input-group.tsx"
      >
        <div className="grid gap-3 md:grid-cols-2">
          <InputGroup>
            <InputGroupAddon align="inline-start">
              <Search className="size-4 text-muted-foreground" />
            </InputGroupAddon>
            <InputGroupInput placeholder="Rechercher..." />
          </InputGroup>
          <InputGroup>
            <InputGroupInput placeholder="https://" />
            <InputGroupAddon align="inline-end">
              <InputGroupText>.opera-off.fr</InputGroupText>
            </InputGroupAddon>
          </InputGroup>
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="hover-card"
        title="HoverCard"
        description="Aperçu au survol. Préférer Tooltip pour du texte court."
        importPath={`import { HoverCard, HoverCardContent, HoverCardTrigger } from "@components/ui/hover-card";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/hover-card.tsx"
        useWhen="Aperçu d'une entité avec contenu riche ou interactif (titre, métadonnée, liens)."
        avoidWhen="Étiquette courte de quelques mots — utiliser Tooltip plus léger."
      >
        <HoverCard>
          <HoverCardTrigger asChild>
            <Button variant="link">Survoler @opera-off</Button>
          </HoverCardTrigger>
          <HoverCardContent>
            <div className="space-y-1">
              <h4 className="text-sm font-semibold">OPERA Off</h4>
              <p className="text-xs text-muted-foreground">Plateforme de formation musicale.</p>
            </div>
          </HoverCardContent>
        </HoverCard>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="multi-select-popover"
        title="MultiSelectPopover"
        description="Sélection multiple avec recherche, déclenchée par un bouton."
        importPath={`import { MultiSelectPopover } from "@components/ui/multi-select-popover";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/multi-select-popover.tsx"
      >
        <div className="max-w-sm">
          <MultiSelectPopover
            options={MULTI_OPTIONS}
            selected={multi}
            onSelectedChange={setMulti}
            placeholder="Choisir des rôles"
            selectedLabel="rôle(s)"
            searchPlaceholder="Rechercher..."
          />
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="multi-select-filter"
        title="MultiSelectFilter"
        description="Filtre à facettes — utilisé dans les barres de filtres de DataTable."
        importPath={`import { MultiSelectFilter } from "@components/ui/multi-select-filter";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/multi-select-filter.tsx"
      >
        <MultiSelectFilter title="Rôle" options={MULTI_OPTIONS} selectedValues={facets} onSelectedValuesChange={setFacets} />
      </ShowcaseBlock>

      <ShowcaseBlock
        id="read-only-field"
        title="ReadOnlyField"
        description="Affichage d'une valeur non-éditable, mise en page comme un input."
        importPath={`import { ReadOnlyField } from "@components/ui/read-only-field";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/read-only-field.tsx"
      >
        <div className="grid max-w-md gap-3">
          <ReadOnlyField label="Email" inline>
            marie.dubois@example.com
          </ReadOnlyField>
          <ReadOnlyField label="Notes">
            <p>Ligne 1</p>
            <p>Ligne 2</p>
          </ReadOnlyField>
        </div>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="scroll-area"
        title="ScrollArea"
        description="Zone défilable avec scrollbar stylée."
        importPath={`import { ScrollArea } from "@components/ui/scroll-area";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/scroll-area.tsx"
      >
        <ScrollArea className="h-32 w-full rounded-md border p-3">
          <div className="space-y-1 text-sm">
            {Array.from({ length: 20 }).map((_, i) => (
              <p key={i}>Élément {i + 1}</p>
            ))}
          </div>
        </ScrollArea>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="table"
        title="Table"
        description="Tableau brut — préférer DataTable pour le tri/recherche/pagination intégrés."
        importPath={`import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@components/ui/table";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/table.tsx"
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Rôle</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {SAMPLE_ROWS.map(r => (
              <TableRow key={r.id}>
                <TableCell>{r.name}</TableCell>
                <TableCell>{r.role}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ShowcaseBlock>

      <ShowcaseBlock
        id="empty"
        title="Empty"
        description="Bloc empty state composable — bordure pointillée, contenu centré."
        importPath={`import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@components/ui/empty";`}
        sourcePath="src/shared-kernel/primary/shared/shadcn/ui/empty.tsx"
      >
        <Empty className="border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <FolderOpen className="size-6" />
            </EmptyMedia>
            <EmptyTitle>Aucun dossier</EmptyTitle>
            <EmptyDescription>Importez ou créez un dossier pour commencer.</EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button size="sm">
              <PlusCircle className="mr-2 size-4" />
              Créer un dossier
            </Button>
          </EmptyContent>
        </Empty>
      </ShowcaseBlock>
    </div>
  );
};
